import React, { useMemo } from 'react';

import Button from 'components/partials/button/button';
import ActionsDropdown from 'components/partials/actions-dropdown/actions-dropdown';

import MetricCard from './metric-card';

import Icons from 'assets/icons';

import type { ActionsDropdownOption } from 'components/partials/actions-dropdown/actions-dropdown';
import type { MetricCardData } from '../results-step.types';

interface MetricsSectionProps {
  cards: MetricCardData[];
  actualYearLabel: string;
  isRecalculating?: boolean;
  onShowCoa: () => void;
  isCoaDisabled?: boolean;
  onCompareEstimates?: () => void;
  canCompare?: boolean;
  onDownloadDataset: () => void;
  isDownloadingDataset?: boolean;
  canDownloadDataset?: boolean;
}

const MetricsSection = ({
  cards,
  actualYearLabel,
  isRecalculating = false,
  onShowCoa,
  isCoaDisabled,
  onCompareEstimates,
  canCompare = false,
  onDownloadDataset,
  isDownloadingDataset = false,
  canDownloadDataset = false,
}: MetricsSectionProps): React.JSX.Element => {
  const dropdownOptions: ActionsDropdownOption[] = useMemo(
    () => [
      {
        label: 'Compare estimates',
        onClick: onCompareEstimates,
        isDisabled: !canCompare,
        customClass: 'font-normal text-gray-700',
      },
    ],
    [onCompareEstimates, canCompare]
  );

  return (
    <section className="flex flex-col gap-4" data-testid="results-metrics-section">
      <div className="flex items-center gap-3">
        <h3 className="flex-1 text-lg font-bold text-gray-700">
          Total first year estimate
        </h3>

        <Button
          type="button"
          variant="reserve-outlined"
          size="sm"
          onClick={onShowCoa}
          disabled={isCoaDisabled}
          className="shadow-sm"
          data-testid="results-show-coa-button"
        >
          Show CoA
        </Button>

        {canDownloadDataset && (
          <Button
            type="button"
            variant="brand-outlined"
            size="sm"
            onClick={onDownloadDataset}
            loading={isDownloadingDataset}
            iconAfter={<Icons.Download aria-hidden="true" />}
            className="shadow-sm"
            data-testid="results-download-dataset-button"
          >
            Download dataset
          </Button>
        )}

        <ActionsDropdown
          options={dropdownOptions}
          variant="brand-text"
          customClass="size-8 p-1.5 border-none aria-expanded:bg-lime-100"
          menuCustomClass="border-none shadow-md p-1.5 gap-0"
        />
      </div>

      <div className="flex flex-col gap-3 tablet:flex-row">
        {cards.map((card) => (
          <MetricCard
            key={card.key}
            card={card}
            actualYearLabel={actualYearLabel}
            isLoading={isRecalculating}
          />
        ))}
      </div>
    </section>
  );
};

export default MetricsSection;
